import { Layout } from "Layout";
import CalendarSetting from "pages/Settings/CalendarSetting";
import React, { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import checkAccess from "utils/checkAccess";

// Pages
const AdminDashbord = lazy(() => import("pages/Dashboard"));
const RolesAdd = lazy(() => import("pages/MembersAndRoles/Roles/RolesAdd"));
const RolesEdit = lazy(() => import("pages/MembersAndRoles/Roles/RolesEdit"));

const TeamMembers = lazy(() => import("pages/MembersAndRoles/TeamMembers"));
const AddTeamMember = lazy(
  () => import("pages/MembersAndRoles/TeamMembers/AddTeamMember"),
);
const EditTeamMember = lazy(
  () => import("pages/MembersAndRoles/TeamMembers/EditTeamMember"),
);
const LogsList = lazy(() => import("pages/Users/UserDetail/LogsList"));
const Language = lazy(() => import("pages/DataManagement/Language"));
const AdminProfile = lazy(() => import("pages/AdminProfile/Profile"));
const AdminChangePass = lazy(() => import("pages/AdminProfile/ChangePass"));
const RejectReason = lazy(() => import("pages/DataManagement/RejectReason"));
const SequrityQuestion = lazy(
  () => import("pages/DataManagement/SequrityQuestion"),
);
const Holiday = lazy(() => import("pages/DataManagement/Holiday"));
const DefaultPreferences = lazy(
  () => import("pages/DataManagement/DefaultPreferences"),
);
const Signature = lazy(() => import("pages/DataManagement/Signature"));
const CompanyInfo = lazy(() => import("pages/Settings/CompanyInfo"));

const AppVersion = lazy(() => import("pages/Settings/AppVersion"));
const PaginationSettings = lazy(
  () => import("pages/Settings/General/PaginationSettings"),
);
const OtherSettings = lazy(
  () => import("pages/Settings/General/OtherSettings"),
);
const FileSizeSetting = lazy(
  () => import("pages/Settings/General/FileSizeSetting"),
);
const Storage = lazy(() => import("pages/Settings/General/storage"));
const Maintenance = lazy(() => import("pages/Settings/Maintenance"));
const MasterKeySetup = lazy(() => import("pages/Settings/MasterKeySetup"));
const DriveSetting = lazy(() => import("pages/Settings/DriveSetting"));
const ChatSetting = lazy(() => import("pages/Settings/ChatSetting"));

const Composing = lazy(
  () => import("pages/DataManagement/DefaultPreferences/Composing"),
);
const OtherPreferences = lazy(
  () => import("pages/DataManagement/DefaultPreferences/OtherPreferences"),
);
const Appearance = lazy(
  () => import("pages/DataManagement/DefaultPreferences/Appearance"),
);
const EmailPrivacy = lazy(
  () => import("pages/DataManagement/DefaultPreferences/EmailPrivacy"),
);
const ChatPreferences = lazy(
  () => import("pages/DataManagement/DefaultPreferences/ChatPreferences"),
);
const General = lazy(
  () => import("pages/DataManagement/DefaultPreferences/General"),
);
const LanguageTime = lazy(
  () => import("pages/DataManagement/DefaultPreferences/LanguageTime"),
);

const Messages = lazy(
  () => import("pages/DataManagement/DefaultPreferences/Messages"),
);

const Country = lazy(() => import("pages/DataManagement/Country"));
const AndroidApp = lazy(
  () => import("pages/DataManagement/GetTheApp/AndroidApp"),
);
const IOSApp = lazy(() => import("pages/DataManagement/GetTheApp/IOSApp"));
const Category = lazy(() => import("pages/DataManagement/Category"));
const Color = lazy(() => import("pages/DataManagement/Color"));
const TermAndCondition = lazy(() => import("pages/CMS/TermAndCondition"));
const PrivacyPolicy = lazy(() => import("pages/CMS/PrivacyPolicy"));
const CommonIssue = lazy(() => import("pages/CMS/CommonIssue"));
const ReportProblem = lazy(() => import("pages/CMS/ReportProblem"));
const ReportChatUserList = lazy(() => import("pages/CMS/ReportChatUserList"));
const AdminSettings = lazy(() => import("pages/Settings"));
const Permission = lazy(() => import("pages/MembersAndRoles/Permission"));
const AddPermission = lazy(
  () => import("pages/MembersAndRoles/Permission/AddPermission"),
);
const Roles = lazy(() => import("pages/MembersAndRoles/Roles"));
const ForgetPassword = lazy(() => import("pages/ForgetPassword"));
const Error404 = lazy(() => import("pages/ErrorPages/Error404"));
const Unauthorized = lazy(() => import("pages/ErrorPages/Unauthorized"));

const ResetPassSuccessfully = lazy(() => import("pages/ResetPassSuccessfully"));
const ResetPasswordAdmin = lazy(() => import("pages/ResetPasswordAdmin"));
const AdminLogin = lazy(() => import("pages/Login/Login"));
const UsersPage = lazy(() => import("pages/Users"));
const ReviewsPage = lazy(() => import("pages/Reviews"));
const UserDetail = lazy(() => import("pages/Users/UserDetail"));
const UsersFilters = lazy(() => import("pages/Users/UsersFilters"));
const UserPlan = lazy(() => import("pages/DataManagement/UserPlan"));
const AddUserPlan = lazy(
  () => import("pages/DataManagement/UserPlan/AddUserPlan"),
);

const Feedback = lazy(() => import("pages/Feedback"));
const HelpAndFeedback = lazy(
  () => import("pages/DataManagement/HelpAndFeedback"),
);
const TimeZone = lazy(() => import("pages/DataManagement/TimeZone"));
const Emoji = lazy(() => import("pages/DataManagement/Emoji"));
const TermsAndConditionHistory = lazy(
  () => import("pages/CMS/TermsAndConditionHistory"),
);
const MaintenanceMode = lazy(() => import("pages/Maintanence"));
const Announcement = lazy(() => import("pages/Announcement"));

const basePath = "";

const generatePath = (slug) => {
  return `${basePath}/${slug}`;
};

export const adminRoutes = [
  {
    path: generatePath("dashboard"),
    element: <AdminDashbord />,
    title: "Dashboard",
  },
  {
    path: generatePath("announcement"),
    element: <Announcement />,
    title: "Announcement",
    id: "announcement",
  },
  {
    path: generatePath("error-404"),
    element: <Error404 />,
  },
  {
    path: generatePath("settings"),
    element: <AdminSettings />,
    title: "Settings",
  },
  {
    path: generatePath("terms"),
    element: <TermAndCondition />,
    title: "Terms And Conditions",
  },
  {
    path: generatePath("terms-history"),
    element: <TermsAndConditionHistory />,
    title: "Terms And Conditions History",
  },
  {
    path: generatePath("privacy"),
    element: <PrivacyPolicy />,
    title: "Privacy Policy",
  },
  {
    path: generatePath("common/issue"),
    element: <CommonIssue />,
    title: "Common Issues",
    id: "common_issue",
  },
  {
    path: generatePath("report/chat"),
    element: <ReportChatUserList />,
    title: "Reported Chats",
    id: "report_chat_user",
  },
  {
    path: generatePath("report/problem"),
    element: <ReportProblem />,
    title: "Report Problem Mail",
    id: "report_problem",
  },
  {
    path: generatePath("/public-profile/profile"),
    element: <AdminProfile />,
    title: "My Profile",
  },
  {
    path: generatePath("/public-profile/change-password"),
    element: <AdminChangePass />,
    title: "Change Password",
  },
  {
    path: generatePath("/access-management/permission"),
    element: <Permission />,
    title: "Permissions",
    id: "permission",
  },
  {
    path: generatePath("/access-management/permission/addEdit-permission"),
    element: <AddPermission />,
    title: "Add Permission",
    action: "create",
    id: "permission",
  },
  {
    path: generatePath("/access-management/roles"),
    element: <Roles />,
    title: "Roles",
    id: "roles",
  },
  {
    title: "Team Members",
    path: generatePath("/access-management/team-members"),
    element: <TeamMembers />,
    id: "team_member",
  },
  {
    path: generatePath("/access-management/team-members/add-team-members"),
    element: <AddTeamMember />,
    title: "Add Team Member",
    action: "create",
    id: "team_member",
  },
  {
    path: generatePath("/access-management/team-members/edit-team-members"),
    element: <EditTeamMember />,
    title: "Edit Team Member",
    action: "update",
    id: "team_member",
  },
  {
    title: "Language",
    path: generatePath("/data-management/language"),
    element: <Language />,
    id: "language",
  },
  {
    title: "Add Role",
    path: generatePath("/access-management/roles/add"),
    element: <RolesAdd />,
    action: "create",
    id: "roles",
  },
  {
    title: "Edit Role",
    path: generatePath("/access-management/roles/edit/:id"),
    element: <RolesEdit />,
    action: "update",
    id: "roles",
  },
  {
    title: "Report Phishing Reasons",
    path: generatePath("/data-management/report-phishing/:type"),
    element: <RejectReason />,
    id: "phishing_reason",
  },
  {
    title: "Report Chat Reasons",
    path: generatePath("/data-management/report-chat/:type"),
    element: <RejectReason />,
    id: "report_chat",
  },
  {
    title: "Delete Account Reasons",
    path: generatePath("/data-management/delete-account/:type"),
    element: <RejectReason />,
    id: "reject_reason",
  },
  {
    title: "Security Questions",
    path: generatePath("/data-management/security-question"),
    element: <SequrityQuestion />,
    id: "security_question",
  },
  {
    title: "Countries",
    path: generatePath("/data-management/country"),
    element: <Country />,
    id: "country",
  },
  {
    title: "Label & Folder Colors",
    path: generatePath("/data-management/color"),
    element: <Color />,
    id: "colors",
  },
  {
    title: "IOS Apps",
    path: generatePath("/get-the-app/ios"),
    element: <IOSApp />,
    id: "get-ios-app",
  },
  {
    title: "Company Info",
    path: generatePath("/companyInfo"),
    element: <CompanyInfo />,
    id: "company-info",
  },
  {
    title: "Master Key Setup",
    path: generatePath("/master-key-setup"),
    element: <MasterKeySetup />,
    id: "master-key-setup",
  },
  {
    title: "App Version",
    path: generatePath("/app-version"),
    element: <AppVersion />,
    id: "app-version",
  },
  {
    title: "Calendar Setting",
    path: generatePath("/calendar-setting"),
    element: <CalendarSetting />,
    id: "company-setting",
  },
  {
    title: "Composing",
    path: generatePath("/composing"),
    element: <Composing />,
    id: "composing",
  },
  {
    title: "Storage",
    path: generatePath("/storage"),
    element: <Storage />,
    id: "storage",
  },
  {
    title: "Language & Time",
    path: generatePath("/language-time"),
    element: <LanguageTime />,
    id: "language-time",
  },
  {
    title: "Email privacy",
    path: generatePath("/email-privacy"),
    element: <EmailPrivacy />,
    id: "email-privacy",
  },
  {
    title: "Chat Preferences",
    path: generatePath("/chat-preferences"),
    element: <ChatPreferences />,
    id: "chat-preferences",
  },
  {
    title: "Appearance",
    path: generatePath("/appearance"),
    element: <Appearance />,
    id: "appearance",
  },
  {
    title: "General",
    path: generatePath("/general"),
    element: <General />,
    id: "general",
  },
  {
    title: "Other Settings",
    path: generatePath("/other-settings"),
    element: <OtherSettings />,
    id: "other-settings",
  },
  {
    title: "FileSize Setting",
    path: generatePath("/fil-size-setting"),
    element: <FileSizeSetting />,
    id: "fil-size-setting",
  },
  {
    title: "Messages",
    path: generatePath("/messages"),
    element: <Messages />,
    id: "messages",
  },
  {
    title: "Maintenance",
    path: generatePath("/maintenance"),
    element: <Maintenance />,
    id: "maintenance",
  },
  {
    title: "Other Preferences",
    path: generatePath("/other-preferences"),
    element: <OtherPreferences />,
    id: "other-preferences",
  },
  {
    title: "Pagination Settings",
    path: generatePath("/pagination-setting"),
    element: <PaginationSettings />,
    id: "pagination-setting",
  },
  {
    title: "Drive Setting",
    path: generatePath("/drive-file-setting"),
    element: <DriveSetting />,
    id: "pagination-setting",
  },
  {
    title: "Chat Setting",
    path: generatePath("/chat-setting"),
    element: <ChatSetting />,
    id: "pagination-setting",
  },
  {
    title: "Android Apps",
    path: generatePath("/get-the-app/android"),
    element: <AndroidApp />,
    id: "get-android-app",
  },
  {
    title: "User Plan",
    path: generatePath("/data-management/user-plan"),
    element: <UserPlan />,
    id: "user_plan",
  },
  {
    title: "Add Plan",
    path: generatePath("/data-management/user-plan/add"),
    element: <AddUserPlan />,
    id: "add_user_plan",
  },
  {
    title: "Edit Plan",
    path: generatePath("/data-management/user-plan/edit"),
    element: <AddUserPlan />,
    id: "edit_user_plan",
  },
  {
    title: "Holiday",
    path: generatePath("/data-management/holiday"),
    element: <Holiday />,
    id: "holiday",
  },
  {
    title: "Default Preferences",
    path: generatePath("/defaultPreferences"),
    element: <DefaultPreferences />,
    id: "dSetting",
  },
  {
    title: "Signature",
    path: generatePath("/data-management/signature"),
    element: <Signature />,
    id: "signature",
  },
  {
    title: "Report Categories",
    path: generatePath("/data-management/report-category"),
    element: <Category />,
    id: "report_category",
  },
  {
    title: "Help And Feedback",
    path: generatePath("/data-management/help-and-feedback"),
    element: <HelpAndFeedback />,
    id: "help_and_feedback",
  },
  {
    title: "TimeZone",
    path: generatePath("/data-management/timezone"),
    element: <TimeZone />,
    id: "timezone",
  },
  {
    title: "Emoji",
    path: generatePath("/data-management/emoji"),
    element: <Emoji />,
    id: "emoji",
  },
  {
    title: "Users",
    path: generatePath("/users"),
    element: <UsersPage />,
    id: "users",
  },
  {
    title: "Reviews",
    path: generatePath("/reviews"),
    element: <ReviewsPage />,
    id: "review",
  },
  {
    title: "User Details",
    path: generatePath("/users/detail/:id"),
    element: <UserDetail />,
    id: "user_detail",
  },
  {
    title: "Filters",
    path: generatePath("/users/detail/:id/filter/:type"),
    element: <UsersFilters />,
    id: "user_detail",
  },
  {
    title: "Day Logs",
    path: generatePath("/users/detail/:id/day/allLogs"),
    element: <LogsList />,
    id: "day_logs",
  },
  {
    title: "Feedback",
    path: generatePath("/feedback"),
    element: <Feedback />,
    id: "feedback",
  },
  {
    title: "Maintenance Mode",
    path: generatePath("/maintenance-mode"),
    element: <MaintenanceMode />,
    id: "maintenance_mode",
  },
];

const adminAuthRoutes = [
  {
    path: generatePath("forget-password"),
    element: <ForgetPassword />,
  },
  {
    path: generatePath("resetpassword/successfully"),
    element: <ResetPassSuccessfully />,
  },
  {
    path: generatePath("resetAdminPassword/:id"),
    element: <ResetPasswordAdmin />,
  },
  {
    path: generatePath("/"),
    element: <AdminLogin />,
  },
];

const AdminNavigation = () => {
  const user = JSON.parse(localStorage.getItem("bigfoot-admin"));

  return (
    <Routes>
      {/* Root redirect */}
      <Route
        path=""
        element={!user ? <AdminLogin /> : <Navigate to="dashboard" replace />}
      />

      {/* Authenticated layout – Layout stays mounted, only Outlet content changes */}
      {user ? (
        <Route element={<Layout />}>
          {adminRoutes.map((route, index) => (
            <Route
              key={`${route.path}${index}`}
              path={route.path}
              element={
                route?.id ? (
                  checkAccess(`${route?.action ?? "read"}_${route.id}`) ? (
                    route.element
                  ) : (
                    <Navigate to="/unauthorized" replace />
                  )
                ) : (
                  route.element
                )
              }
            />
          ))}
        </Route>
      ) : (
        <Route element={<Navigate to="/" replace />} />
      )}

      {/* Public auth routes */}
      {adminAuthRoutes.map((route, index) => (
        <Route
          key={`${route.path}${index}`}
          path={route.path}
          element={route.element}
        />
      ))}

      <Route
        path="/unauthorized"
        element={!user ? <Navigate to="/" replace /> : <Unauthorized />}
      />
      <Route
        path="*"
        element={!user ? <Navigate to="/" replace /> : <Error404 />}
      />
    </Routes>
  );
};

export default AdminNavigation;
